import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Download, Loader2 } from 'lucide-react';

/**
 * FilterBar — 受訪者名單篩選列
 *
 * props:
 *   filters       { source, campaignId, completed, search }
 *   onChange      (patch) => void   — partial update
 *   campaigns     [{id, name}]      — 用來填 campaign 下拉
 *   onExport      () => Promise     — 觸發 CSV 下載（帶 token 的 fetch，非 <a href>）
 */
export function FilterBar({ filters, onChange, campaigns = [], onExport }) {
    const { t } = useTranslation('survey');
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            await onExport();
        } catch (e) {
            console.error('[survey] CSV export failed', e);
            alert(t('filter.exportFailed', { defaultValue: '匯出失敗' }) + (e?.message ? `：${e.message}` : ''));
        } finally {
            setExporting(false);
        }
    };
    return (
        <div className="bg-white border border-cream-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-3">
            {/* 來源 */}
            <Select
                label={t('filter.source')}
                value={filters.source || ''}
                onChange={(v) => onChange({ source: v || undefined })}
                options={[
                    { value: '', label: t('common:state.all') },
                    { value: 'preview', label: t('source.previewCall') },
                    { value: 'sip', label: t('source.single') },
                    { value: 'sip_campaign', label: t('source.campaign') },
                ]}
            />

            {/* Campaign */}
            <Select
                label={t('filter.campaign')}
                value={filters.campaignId || ''}
                onChange={(v) => onChange({ campaignId: v || undefined })}
                options={[
                    { value: '', label: t('common:state.all') },
                    ...campaigns.map((c) => ({ value: c.id, label: c.name || c.id.slice(0, 8) })),
                ]}
            />

            {/* 完成度 */}
            <Select
                label={t('filter.completion')}
                value={filters.completed || ''}
                onChange={(v) => onChange({ completed: v || undefined })}
                options={[
                    { value: '', label: t('common:state.all') },
                    { value: 'true', label: t('filter.completed') },
                    { value: 'false', label: t('filter.incomplete') },
                ]}
            />

            {/* 搜尋 */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                <input
                    type="text"
                    placeholder={t('filter.searchPlaceholder')}
                    value={filters.search || ''}
                    onChange={(e) => onChange({ search: e.target.value || undefined })}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                />
            </div>

            {/* 匯出 */}
            {onExport && (
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink-700 bg-cream-100 hover:bg-cream-200 border border-cream-300 rounded-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {exporting
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Download size={13} />}
                    {t('filter.exportCsv')}
                </button>
            )}
        </div>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <label className="flex items-center gap-2 text-xs text-ink-500">
            <span className="mono-caption">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="px-2.5 py-1.5 text-xs bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
            >
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}
